import {makeAutoObservable} from "mobx";
import {api, catchApi} from "../../api";
import {validationStore} from "../ValidationStore";
import {equals} from "../../utils";
import {FmsFormData} from "../../components/page/fms/FmsCreateForm";
import {FmsCustomerItem} from "./FmsCustomerStore";
import {
    DocumentTypeDto,
    ShipmentOrderDto,
    ShipmentProcessTypeDto,
    ShipmentTypeDto,
    ShippingCarrierTypeDto
} from "../../api/fms";

const emptyObject = {
    name: '',
    displayName: '',
    type: undefined,
}

class FmsCreateStore {
    get billable(): boolean {
        return this._billable;
    }

    set billable(value: boolean) {
        this._billable = value;
    }
    get file(): File | undefined {
        return this._file;
    }

    set file(value: File | undefined) {
        this._file = value;
    }

    get completeShipping(): boolean {
        return this._completeShipping;
    }

    set completeShipping(value: boolean) {
        this._completeShipping = value;
    }

    get shipmentType(): ShipmentTypeDto {
        return this._shipmentType;
    }

    set shipmentType(value: ShipmentTypeDto) {
        this._shipmentType = value;
    }

    get phone(): string {
        return this._phone;
    }

    set phone(value: string) {
        this._phone = value;
    }

    get mail(): string {
        return this._mail;
    }

    set mail(value: string) {
        this._mail = value;
    }

    get line2(): string {
        return this._line2;
    }

    set line2(value: string) {
        this._line2 = value;
    }

    get line1(): string {
        return this._line1;
    }

    set line1(value: string) {
        this._line1 = value;
    }

    get street(): string {
        return this._street;
    }

    set street(value: string) {
        this._street = value;
    }

    get postCode(): string {
        return this._postCode;
    }

    set postCode(value: string) {
        this._postCode = value;
    }

    get houseNumber(): string {
        return this._houseNumber;
    }

    set houseNumber(value: string) {
        this._houseNumber = value;
    }

    get country(): string {
        return this._country;
    }

    set country(value: string) {
        this._country = value;
    }

    get addition(): string {
        return this._addition;
    }

    set addition(value: string) {
        this._addition = value;
    }

    get city(): string {
        return this._city;
    }

    set city(value: string) {
        this._city = value;
    }

    get process(): ShipmentProcessTypeDto {
        return this._process;
    }

    set process(value: ShipmentProcessTypeDto) {
        this._process = value;
    }

    get documentType(): DocumentTypeDto[] {
        return this._documentType;
    }

    set documentType(value: DocumentTypeDto[]) {
        this._documentType = value;
    }

    get vin(): string {
        return this._vin;
    }

    set vin(value: string) {
        this._vin = value;
    }

    get customer(): FmsCustomerItem | undefined {
        return this._customer;
    }

    set customer(value: FmsCustomerItem | undefined) {
        this._customer = value;
    }

    get creating(): boolean {
        return this._creating;
    }

    set creating(value: boolean) {
        this._creating = value;
    }

    get valid(): boolean {
        return this._valid;
    }

    set valid(value: boolean) {
        this._valid = value;
    }

    get dirty(): boolean {
        const current = this.buildOrderDto()
        return !equals(emptyObject, current)
    }

    private _customer: FmsCustomerItem | undefined
    private _vin: string = ''
    private _documentType: DocumentTypeDto[] = []
    private _process: ShipmentProcessTypeDto = ShipmentProcessTypeDto.REGULAR
    private _city: string = ''
    private _addition: string = ''
    private _country: string = ''
    private _houseNumber: string = ''
    private _postCode: string = ''
    private _street: string = ''
    private _line1: string = ''
    private _line2: string = ''
    private _mail: string = ''
    private _phone: string = ''
    private _shipmentType: ShipmentTypeDto = ShipmentTypeDto.MORNING
    private _completeShipping: boolean = false
    private _billable: boolean = true
    private _file: File | undefined

    private _creating: boolean = false
    private _valid: boolean = false

    constructor() {
        makeAutoObservable(this)
    }

    get formData(): FmsFormData {
        const {
            completeShipping,
            billable,
            customer,
            vin,
            city,
            addition,
            country,
            houseNumber,
            postCode,
            street,
            line1,
            line2,
            mail,
            phone,
            shipmentType,
            process,
            documentType,
            file
        } = this

        return {
            completeShipping,
            billable,
            vin,
            customer,
            city,
            addition,
            country,
            houseNumber,
            postCode,
            street,
            line1,
            line2,
            mail,
            phone,
            shipmentType,
            process,
            documentType,
            file
        }
    }

    set formData(data: FmsFormData) {
        const {
            completeShipping,
            billable,
            vin,
            customer,
            city,
            addition,
            country,
            houseNumber,
            postCode,
            street,
            line1,
            line2,
            mail,
            phone,
            shipmentType,
            process,
            documentType,
            file
        } = data

        this.completeShipping = completeShipping
        this.billable = billable
        this.vin = vin
        this.customer = customer
        this.city = city
        this.addition = addition
        this.country = country
        this.houseNumber = houseNumber
        this.postCode = postCode
        this.street = street
        this.line1 = line1
        this.line2 = line2
        this.mail = mail
        this.phone = phone
        this.shipmentType = shipmentType
        this.process = process
        this.documentType = completeShipping ? [] : documentType
        this.file = file
    }

    async create() {
        this.creating = true
        const dto = await this.buildOrderDto()
        if (!dto) {
            return
        }
        const result = await catchApi(async () => await api.fms.api.createShipment({
            shipment: new Blob([JSON.stringify(dto)], {type: 'application/json'}) as any,
            attachment: this.file
        }))
        this.creating = false
        return Boolean(result)
    }

    async validate() {
        this.valid = false
        const dto = await this.buildOrderDto()
        if (!dto) {
            return
        }
        await validationStore.validate(api.fms.api.validateShipment, dto)
        this.valid = Boolean(validationStore.valid)
    }

    public reset() {
        this.completeShipping = false
        this.billable = true
        this.customer = undefined
        this.vin = ''
        this.documentType = []
        this.process = ShipmentProcessTypeDto.REGULAR
        this.city = ''
        this.addition = ''
        this.country = ''
        this.houseNumber = ''
        this.postCode = ''
        this.street = ''
        this.line1 = ''
        this.line2 = ''
        this.mail = ''
        this.phone = ''
        this.shipmentType = ShipmentTypeDto.MORNING
        this.file = undefined
    }


    private async buildOrderDto(): Promise<ShipmentOrderDto | undefined> {
        const {
            completeShipping,
            billable,
            customer,
            vin,
            documentType,
            process,
            city,
            addition,
            country,
            houseNumber,
            postCode,
            street,
            line1,
            line2,
            mail,
            phone,
            shipmentType,
        } = this

        const carrier = shipmentType === ShipmentTypeDto.POST ? ShippingCarrierTypeDto.POST : ShippingCarrierTypeDto.DHL

        return {
            customerId: customer?.id!!,
            vin,
            documentType: completeShipping ? undefined : documentType,
            process,
            shipmentType,
            carrier,
            billable,
            receiver: {
                address: {
                    city,
                    addition: addition || undefined,
                    country,
                    houseNumber: houseNumber || undefined,
                    postCode,
                    street
                },
                line1,
                line2: line2 || undefined,
                mail: mail || undefined,
                phone: phone || undefined
            },
        }
    }
}

const fmsCreateStore = new FmsCreateStore()

export {
    fmsCreateStore
}
